// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

// CC-Common imports
import { IconConstants, Utils } from '@therealmanage/ciranet-ui-common';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Component imports
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { HelperService } from '../../_services/shared-services/helper.service';

// Model imports
import { AddContactInfoCustomFieldRawDto } from '../../_models/registration/add-contact-info-custom-field-raw-dto';
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { ResultStatusDto } from '../../_models/result-status-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';
import { AddContactInfoRawDto } from '../../_models/registration/add-contact-info-raw-dto';

// Constant imports
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';

@Component({
  selector: 'app-signup-text-options',
  templateUrl: './signup-text-options.component.html',
  styleUrls: ['./signup-text-options.component.scss'],
  standalone: false
})
export class SignupTextOptionsComponent
  extends RecaptchaBaseComponent
  implements OnInit
{
  @Input() userInfo: CreateUserParameterDto;
  @Input() registrationEditor: RegistrationEditorResultDto;

  @Output() onPreviousClick = new EventEmitter<any>();
  @Output() onBackToLogin = new EventEmitter<any>();

  isSmallScreen = false;
  isMediumScreen = false;

  customFieldsData: Array<any> = [];

  loadingVisible = false;
  isUserCreated = false;
  showErrorMessage = false;
  errorMessage = '';

  phonePattern: RegExp = ValidationFormatConstants.phonePattern;
  phoneEditorOptions = {
    mode: 'tel',
    mask: '(X00) 000-0000',
    maskRules: ValidationFormatConstants.phoneRules,
    useMaskedValue: true,
    maskInvalidMessage: null
  };
  phoneRules: RegExp = ValidationFormatConstants.phoneRules;
  userRecaptchaToken: string;
  mobileNumber: string;
  backToLoginIcon = IconConstants.BACK_TO_LOGIN;

  isRecaptchaResolved = false;

  // Text preference toggles
  standardNoticesEnabled = false;
  emergencyNoticesEnabled = true;

  get isAnyNotificationEnabled(): boolean {
    return this.standardNoticesEnabled || this.emergencyNoticesEnabled;
  }

  onNotificationToggleChanged(): void {
    // Clear mobile number validation error when all notifications are disabled
    if (!this.isAnyNotificationEnabled && this.showErrorMessage) {
      this.showErrorMessage = false;
      this.errorMessage = '';
    }
  }

  constructor(
    private screenService: ScreenService,
    private userService: UserService,
    private helperService: HelperService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit() {
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    // Update header title
    setTimeout(() => {
      this.helperService.subjectLoginHeaderText.next(
        'Text&nbsp;Communication Preferences'
      );
    }, 0);

    this.userRecaptchaToken = this.userInfo.recaptchaToken;
    this.prepareCommunications();

    this.checkRecaptchaValidation();
  }

  btnPreviousClick(): void {
    this.prepareCommunicationsCustomFields();

    // Validate mobile number format only if provided
    if (this.mobileNumber && !this.validatePhoneNumber()) {
      return;
    }

    // Reset header title when going back to email options
    this.helperService.subjectLoginHeaderText.next(
      'Email&nbsp;Communication Preferences'
    );
    this.onPreviousClick.emit();
  }

  btnRegisterClick(): void {
    if (!this.userRecaptchaToken && this.isValidRecaptcha()) {
      this.userInfo.recaptchaToken = this.recaptchaToken;
    }
    if (!!this.userInfo.recaptchaToken) {
      this.showErrorMessage = false;
      this.errorMessage = '';
      this.prepareCommunicationsCustomFields();

      // Validate mobile number: required when any notification is enabled
      if (this.isAnyNotificationEnabled) {
        if (!this.mobileNumber) {
          this.showErrorMessage = true;
          this.errorMessage = 'This field is required';
          return;
        }
        if (!this.validatePhoneNumber()) {
          return;
        }
      }

      this.loadingVisible = true;
      RxjsUtils.safeSubscribe(
        this.userService.createUser(this.userInfo),
        (result: ResultStatusDto) => {
          this.resetRecaptcha();
          this.loadingVisible = false;
          if (result.isSuccess == false) {
            this.showErrorMessage = true;
            this.errorMessage =
              result.message || 'There is an error creating user.';
          } else {
            this.isUserCreated = true;
            // Update header for success screen
            this.helperService.subjectLoginHeaderText.next(
              'Registration Successfully Completed'
            );
          }
        },
        (error) => {
          this.resetRecaptcha();
          this.loadingVisible = false;
          this.processApiError(error);
        }
      );
    }
  }

  btnBackToLogin(): void {
    this.helperService.subjectLoginHeaderText.next('Welcome Home!');
    this.onBackToLogin.emit();
  }

  onMobileNumberChanged(): void {
    if (!!this.mobileNumber) {
      this.validatePhoneNumber();
    }
  }

  // Override
  override processApiError(error: any): void {
    const apiError = error.error;
    if (
      apiError &&
      apiError.message &&
      apiError.message.indexOf('captcha') !== -1
    ) {
      this.userRecaptchaToken = null;
      this.userInfo.recaptchaToken = null;
      this.messageBox.showErrorMessage(
        'Invalid Token',
        this.validationErrorMessage
      );
      this.checkRecaptchaValidation();
    } else {
      this.errorService.displayError(error);
    }
  }

  onTextOptionsRecaptchaResolved(event): void {
    this.onRecaptchaResolved(event);
    this.checkRecaptchaValidation();
  }

  private checkRecaptchaValidation(): void {
    this.isRecaptchaResolved =
      this.isValidRecaptcha() || Utils.notNullOrEmpty(this.userRecaptchaToken);
  }

  private validatePhoneNumber(): boolean {
    if (this.phonePattern.test(this.mobileNumber)) {
      this.userInfo.contactInfo.phoneData.content = this.mobileNumber;
      return true;
    } else {
      this.messageBox.showErrorMessage(
        'Error',
        'Please provide valid phone number.'
      );
      return false;
    }
  }

  private prepareCommunicationsCustomFields(): void {
    const customFieldsData: Array<AddContactInfoCustomFieldRawDto> = [];
    this.customFieldsData.forEach((customField) => {
      customFieldsData.push({
        fieldName: customField.fieldName,
        fieldValue: customField.fieldValue
      });
    });

    this.userInfo.contactInfo.phoneData.customFieldsData = customFieldsData;

    if (!this.userInfo.contactInfo.phoneData.subTypeId) {
      this.userInfo.contactInfo.phoneData.subTypeId =
        this.registrationEditor.defaultPhoneSubTypeId;
    }
  }

  private prepareCommunications(): void {
    let phoneData: AddContactInfoRawDto = null;
    if (
      !!this.registrationEditor.defaultPhoneSubTypeId &&
      this.registrationEditor.phoneData.length > 0
    ) {
      phoneData = this.registrationEditor.phoneData.find(
        (d) =>
          d.subTypeId ===
            this.registrationEditor.defaultPhoneSubTypeId.toString() &&
          ((!!this.userInfo.contactInfo.owner &&
            d.contactId === this.userInfo.contactInfo.owner.contactId) ||
            (!!this.userInfo.contactInfo.emailData &&
              d.contactId === this.userInfo.contactInfo.emailData.contactId))
      );
    }

    if (!!phoneData) {
      this.userInfo.contactInfo.phoneData = phoneData;
      this.mobileNumber = phoneData.content;
      this.validatePhoneNumber();
    } else {
      this.userInfo.contactInfo.phoneData = new AddContactInfoRawDto();
      this.userInfo.contactInfo.phoneData.subTypeId =
        this.registrationEditor.defaultPhoneSubTypeId;
    }

    // pre-populate text options with data from "this.userInfo.contactInfo.phoneData.customFieldsData" that has been loaded above.
    if (this.registrationEditor.phoneCustomDataFields.length > 0) {
      this.registrationEditor.phoneCustomDataFields.forEach((field) => {
        let phoneField: AddContactInfoCustomFieldRawDto =
          new AddContactInfoCustomFieldRawDto();
        if (
          !!this.userInfo.contactInfo.phoneData &&
          !!this.userInfo.contactInfo.phoneData.customFieldsData &&
          this.userInfo.contactInfo.phoneData.customFieldsData.length > 0
        ) {
          phoneField =
            this.userInfo.contactInfo.phoneData.customFieldsData.find(
              (d) => d.fieldName.toLowerCase() === field.fieldName.toLowerCase()
            );
        }

        this.customFieldsData.push({
          fieldName: field.fieldName,
          fieldValue:
            !!phoneField &&
            !!phoneField.fieldName &&
            (phoneField.fieldValue === true || phoneField.fieldValue === false)
              ? phoneField.fieldValue
              : true,
          caption: field.caption
        });
      });
    }
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    this.isMediumScreen = this.screenService.isMediumScreen();
  }
}
